import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  escalationGeneralQuery,
  escalationShellQuery,
  escalationTypesQuery,
  rebuildEscalationCron,
  saveEscalationGeneral,
  saveEscalationTypes,
  type EscalationTicketType,
  type EscalationType,
} from '../../api/settingsEaseedeskEscalation'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsCheck } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Escalation settings.
 *
 * Element cu element fata de `version-3/easeedesk_settings/escalation/
 * escalation_settings/index.php` + `general.php` + `list.php`, si gest cu gest
 * fata de cele trei fisiere din
 * `public/js/views/easeedesk_settings/escalation/escalation_settings/`.
 *
 * TREI BUCATI, TREI SALVARI. Butonul de reimprospatat cronul; "General
 * settings" (pe ce tipuri de tichet merge escaladarea si cate minute asteapta
 * un tichet pana poate fi preluat); "Escalation types" (culoarea si bifa
 * fiecarui fel). Fiecare isi cere si isi salveaza datele singura, ca in portal.
 *
 * Cand furnizorul n-a salvat inca nimic, felurile se arata COMUNE si nebifate,
 * cu culoarea lor de fabrica - imperecherea o face capul de api, nu ecranul.
 */
/**
 * `<input type="color">` primeste NUMAI `#rrggbb` cu litere mici; orice
 * altceva - gol, `DDDDDD` fara diez, trei cifre, un nume de culoare - ii
 * arata NEGRU, si negru se salveaza peste culoarea de dinainte la prima
 * apasare pe "Update list". In baza culorile stau scrise de mana, de ani,
 * in ce forma a nimerit fiecare.
 */
function hex(value: string): string {
  const one = String(value ?? '').trim()
  if (/^#[0-9a-fA-F]{6}$/.test(one)) return one.toLowerCase()
  if (/^#[0-9a-fA-F]{3}$/.test(one))
    return `#${one[1]}${one[1]}${one[2]}${one[2]}${one[3]}${one[3]}`.toLowerCase()
  return '#dddddd'
}

function GeneralSettings() {
  const queryClient = useQueryClient()
  const general = useQuery(escalationGeneralQuery())
  const [types, setTypes] = useState<EscalationTicketType[]>([])
  const [pendingTime, setPendingTime] = useState(5)
  const [pendingOn, setPendingOn] = useState(false)

  useEffect(() => {
    if (!general.data) return
    setTypes(general.data.ticket_types)
    setPendingTime(general.data.settings.pending_time)
    setPendingOn(general.data.settings.pending_time_status === 1)
  }, [general.data])

  const save = useMutation({
    mutationFn: () => saveEscalationGeneral(types, pendingTime, pendingOn),
    onSuccess: () => {
      toast('Settings changed.', 'Success')
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'escalation_settings'] })
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (general.error)
    return <LoadFailed what="The general settings" error={general.error} onRetry={() => void general.refetch()} />
  if (!general.data) return <Spinner />

  return (
    <form
      className="change_escalation_settings"
      method="post"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <ul>
        <li
          style={{
            padding: '10px 20px',
            marginBottom: 4,
            borderRadius: 4,
            borderWidth: '1px 6px',
            borderColor: '#ddd',
            borderStyle: 'solid',
          }}
        >
          <div className="row">
            <div className="col-md-4">
              <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
                Activate escalation
              </h5>
            </div>
            <div className="col-md-8">
              {types.length === 0 ? (
                <div className="alert alert-warning" style={{ margin: 0 }}>
                  <i className="fas fa-info-circle" aria-hidden="true"></i> No active ticket types found. You must
                  activate the ticket types first.
                </div>
              ) : (
                types.map((type) => (
                  <div key={type.ID} className="row" style={{ margin: 0 }}>
                    <div className="col-md-2" style={{ padding: '0 0 5px 0' }}>
                      {/*
                       * `od_console.css` da fiecarui `.badge` cerneala inchisa
                       * (`--od-ink-soft`, #5b6672) si macheta scrie doar fundalul, deci
                       * prescurtarea se citea inchis pe culoarea tipului. In portal era
                       * alba, din `.badge` al lui Bootstrap.
                       */}
                      <span className="badge"
                        style={{ minWidth: 23, backgroundColor: type.color_identifier, color: '#fff', textAlign: 'center' }}>
                        {type.short_name}
                      </span>
                    </div>
                    <div className="col-md-6" style={{ padding: '0 0 5px 0' }}>
                      {type.type_name}
                    </div>
                    <div className="col-md-4" style={{ padding: '0 0 5px 0' }}>
                      <div className="pull-right">
                        {/*
                         * Clasa era carligul lui Switchery, plugin pe care
                         * v4 nu-l incarca: nicio regula in foile servite,
                         * deci o casuta goala de 15px plutita la dreapta,
                         * fara nimic langa ea care sa spuna ce comuta.
                         * Acum e bifa zonei, cu cuvantul ei.
                         */}
                        <SettingsCheck
                          label="Escalation"
                          name={`ticket_types[${type.ID}][status]`}
                          checked={type.checked === 1}
                          onChange={(on) =>
                            setTypes((rows) =>
                              rows.map((row) => (row.ID === type.ID ? { ...row, checked: on ? 1 : 0 } : row)),
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </li>

        <li
          style={{
            padding: '10px 20px',
            marginBottom: 4,
            borderRadius: 4,
            borderWidth: '1px 6px',
            borderColor: '#ddd',
            borderStyle: 'solid',
          }}
        >
          <div className="row">
            <div className="col-md-4">
              <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
                Pending time{' '}
                <i
                  className="fas fa-info-circle color-blue font-14"
                  style={{ cursor: 'default' }}
                  title="The time, in minutes, until the assigned technician can take over the ticket"
                  aria-hidden="true"
                ></i>
              </h5>
            </div>
            <div className="col-md-1"></div>
            <div className="col-md-5">
              <input
                type="number"
                className="form-control input-sm input-number"
                min={5}
                value={pendingTime}
                onChange={(e) => setPendingTime(Number(e.target.value))}
              />
            </div>
            <div className="col-md-2">
              <div className="pull-right">
                <SettingsCheck
                  label="Active"
                  name="settings[pending_time_status][value]"
                  checked={pendingOn}
                  onChange={setPendingOn}
                />
              </div>
            </div>
          </div>
        </li>
      </ul>
      <div className="row" style={{ marginTop: 20 }}>
        <div className="col-md-12">
          <button type="submit" className="btn btn-primary pull-right update-escalation-settings" disabled={save.isPending}>
            Update settings
          </button>
        </div>
      </div>
    </form>
  )
}

function EscalationTypes() {
  const queryClient = useQueryClient()
  const list = useQuery(escalationTypesQuery())
  const [types, setTypes] = useState<EscalationType[]>([])

  useEffect(() => {
    if (!list.data) return
    setTypes(list.data.types)
  }, [list.data])

  const save = useMutation({
    mutationFn: () => saveEscalationTypes(types),
    onSuccess: () => {
      toast('Settings changed', 'Success')
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'escalation_settings'] })
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (list.error) return <LoadFailed what="The escalation types" error={list.error} onRetry={() => void list.refetch()} />
  if (!list.data) return <Spinner />

  return (
    <form
      className="change_escalation_types"
      method="post"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      {types.length === 0 && (
        <div className="alert alert-info" style={{ marginBottom: 0 }}>
          <i className="fas fa-info-circle" aria-hidden="true"></i> No escalation types.
        </div>
      )}
      <ul>
        {types.map((type) => (
          <li
            key={type.escalation_type}
            style={{
              padding: '10px 20px',
              marginBottom: 4,
              borderRadius: 4,
              borderWidth: '1px 6px',
              borderColor: '#ddd',
              borderStyle: 'solid',
            }}
          >
            <div className="row">
              <div className="col-md-4">
                <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
                  {type.type_name}
                </h5>
              </div>
              <div className="col-md-1">
                <span
                  className="badge"
                  style={{ minWidth: 23, minHeight: 23, backgroundColor: hex(type.escalation_color) }}
                >
                  {' '}
                </span>
              </div>
              <div className="col-md-5">
                {/*
                 * In macheta veche casuta asta e legata de `colpick` -
                 * alesul de culoare al portalului, pe care v4 nu-l
                 * incarca. Ramasese o casuta de text in care culoarea se
                 * scria de mana, in hexa, si nimic nu spunea ce forma se
                 * asteapta. Acum e alesul browserului, ca pe fila Planning
                 * de alaturi; pastila de langa il urmeaza, exact ce facea
                 * `onChange`-ul lui colpick.
                 */}
                <input
                  type="color"
                  className="form-control escalation-color"
                  name={`types[${type.escalation_type}][color]`}
                  aria-label={`${type.type_name} colour`}
                  value={hex(type.escalation_color)}
                  onChange={(e) =>
                    setTypes((rows) =>
                      rows.map((row) =>
                        row.escalation_type === type.escalation_type
                          ? { ...row, escalation_color: e.target.value }
                          : row,
                      ),
                    )
                  }
                />
              </div>
              <div className="col-md-2">
                <div className="pull-right">
                  <SettingsCheck
                    label="Active"
                    name={`types[${type.escalation_type}][status]`}
                    checked={type.checked === 1}
                    onChange={(on) =>
                      setTypes((rows) =>
                        rows.map((row) =>
                          row.escalation_type === type.escalation_type ? { ...row, checked: on ? 1 : 0 } : row,
                        ),
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="row" style={{ marginTop: 20 }}>
        <div className="col-md-12">
          <button type="submit" className="btn btn-primary pull-right update-escalation-types" disabled={save.isPending}>
            Update list
          </button>
        </div>
      </div>
    </form>
  )
}

export function EscalationSettingsPage() {
  const shell = useQuery(escalationShellQuery())

  const rebuild = useMutation({
    mutationFn: () => rebuildEscalationCron(),
    onSuccess: (answer) =>
      answer.cron === 'failed'
        ? toast(answer.cron_message || 'The cron server did not answer.', 'Error', 'error')
        : toast('Cron refreshed', 'Success'),
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />

  return (
    <div>
      <h4>
        <strong>Escalation</strong> settings
      </h4>
      <hr />

      <section className="settings">
        <div className="row">
          <div className="col-md-12">
            <div className="form-group row">
              <div className="col-md-12">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={rebuild.isPending}
                  onClick={() => rebuild.mutate()}
                >
                  <i className="fas fa-sync-alt" aria-hidden="true"></i> Refresh cron
                </button>
                <span className="help-block">Refresh escalation settings cron after every change.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ width: 300 }}>General settings</h5>
          </div>
          <div className="col-md-10">
            <div className="general-group">
              <GeneralSettings />
            </div>
          </div>
        </div>
        <hr className="dotted" />
      </section>

      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ width: 300 }}>Escalation types</h5>
          </div>
          <div className="col-md-10">
            <div className="list-group">
              <EscalationTypes />
            </div>
          </div>
        </div>
        <hr className="dotted" />
      </section>
    </div>
  )
}
